/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { useNavigate } from 'react-router-dom'

import { mono, monoFont, monoGutter, monoMaxWidth } from './monolith.tokens'
import { MONOLITH_NAV_HEIGHT } from './MonolithNav'
import { productLineList } from '@src/configs/products.config'

export const MONOLITH_PRODUCT_NAV_HEIGHT = 56

interface PropsType {
	/** 지금 보고 있는 제품의 소문자 코드 — 이 항목이 활성 표시된다 */
	activeCode: string
}

/**
 * 제품 목록 서브 네비 — 메인 네비(72) 바로 아래에 붙는다.
 *
 * 라인업 전체를 한 줄에 펴 두고 지금 보는 것만 표시한다. 제품 사이를 오갈 때 매번
 * `/products` 로 되돌아갔다 들어올 필요가 없다. 왼쪽 "Products" 가 인덱스로 돌아가는 길이다.
 *
 * 목록의 SSOT 는 `configs/products.config.ts` 다 — 여기서 제품명을 다시 적지 않는다.
 * 표시명은 13~16px 자리라 워드마크가 아니라 본문 서체다 (PRODUCT_NAMES §4).
 *
 * Buy 는 여기 두지 않는다. 페이지 본문의 히어로와 클로즈 두 곳에 있다.
 */
export const MonolithProductNav = (props: PropsType) => {
	const navigate = useNavigate()
	const listRef = React.useRef<HTMLDivElement>(null)

	/** 라인 구분 없이 한 줄로 편다 — 이 자리에서는 순서만 의미가 있다 */
	const productList = React.useMemo(
		() => productLineList.flatMap(aLine => aLine.products),
		[],
	)

	/**
	 * 좁은 화면에서는 목록이 가로로 넘친다. 활성 항목을 보이는 자리로 당긴다.
	 * `scrollIntoView` 는 창까지 같이 움직이므로 컨테이너의 `scrollLeft` 만 직접 옮긴다.
	 */
	React.useEffect(() => {
		const list = listRef.current
		if (list === null) return

		const active = list.querySelector<HTMLElement>('.mnProductNavItem.on')
		if (active === null) return

		list.scrollLeft = Math.max(
			0,
			active.offsetLeft - (list.clientWidth - active.clientWidth) / 2,
		)
	}, [props.activeCode])

	const go = (to: string) => (e: React.MouseEvent) => {
		e.preventDefault()
		navigate(to)
	}

	return (
		<View css={style} className="mnProductNav">
			<View className="mnProductNavInner">
				<a className="mnProductNavIndex" href="/products" onClick={go('/products')}>
					Products
				</a>

				<nav className="mnProductNavList" ref={listRef} aria-label="Products">
					{productList.map(aProduct => {
						const isActive = aProduct.code === props.activeCode.toLowerCase()
						return (
							<a
								key={aProduct.code}
								className={`mnProductNavItem ${isActive ? 'on' : ''}`}
								href={`/products/${aProduct.code}`}
								onClick={go(`/products/${aProduct.code}`)}
								aria-current={isActive ? 'page' : undefined}
							>
								{aProduct.name}
							</a>
						)
					})}
				</nav>
			</View>
		</View>
	)
}

const style = css({
	position: 'sticky',
	top: MONOLITH_NAV_HEIGHT,
	zIndex: 2,
	width: '100%',
	height: MONOLITH_PRODUCT_NAV_HEIGHT,
	background: mono.bg,
	borderBottom: `1px solid ${mono.line}`,
	fontFamily: monoFont.sans,

	'.mnProductNavInner': {
		width: '100%',
		maxWidth: monoMaxWidth,
		height: '100%',
		margin: '0 auto',
		paddingLeft: monoGutter,
		paddingRight: monoGutter,
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 'clamp(24px, 3vw, 48px)',
	},

	/** 인덱스로 돌아가는 길. 메타 라벨 급이라 12px .18em */
	'.mnProductNavIndex': {
		fontSize: 12,
		letterSpacing: '.18em',
		textTransform: 'uppercase',
		fontWeight: 500,
		color: mono.faint,
		whiteSpace: 'nowrap',
		transition: 'color 200ms ease-out',
		'&:hover': { color: mono.ink },
	},

	'.mnProductNavList': {
		display: 'flex',
		alignItems: 'center',
		gap: 'clamp(16px, 2vw, 32px)',
		minWidth: 0,
		overflowX: 'auto',
		scrollbarWidth: 'none',
		'&::-webkit-scrollbar': { display: 'none' },

		'.mnProductNavItem': {
			fontSize: 13,
			fontWeight: 500,
			color: mono.faint,
			whiteSpace: 'nowrap',
			paddingBottom: 2,
			borderBottom: '1px solid transparent',
			transition: 'color 200ms ease-out',

			'&:hover': { color: mono.ink },
			'&.on': { color: mono.ink, borderBottomColor: mono.ink },
		},
	},

	/** 좁은 화면에서는 목록만 남기고 가로로 흘린다 — 인덱스는 메인 네비의 Products 에도 있다 */
	'@media (max-width: 700px)': {
		'.mnProductNavIndex': { display: 'none' },
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnProductNavItem, .mnProductNavIndex': { transition: 'none' },
	},
})
